import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #gridObserver;

  /** @type {boolean} */
  #isEnsuringPageFilled = false;

  /** @type {number} */
  #maxIterations = 3;

  /** @type {number} */
  #currentIteration = 0;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#fixSearchPagination();

    this.#observeGridChanges();

    setTimeout(() => {
      requestIdleCallback(() => {
        this.#ensurePageIsFilled();
      });
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    if (this.#gridObserver) {
      this.#gridObserver.disconnect();
    }
  }

  #observeGridChanges() {
    const { grid } = this.refs;
    if (!grid) return;

    let timeoutId;
    this.#gridObserver = new MutationObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        requestIdleCallback(() => {
          this.#ensurePageIsFilled();
        });
      }, 150);
    });

    this.#gridObserver.observe(grid, {
      childList: true,
      subtree: false,
    });
  }

  #fixSearchPagination() {
    if (window.location.pathname === '/search' || window.location.pathname.includes('/search')) {
      const url = new URL(window.location.href);
      const hasQuery = url.searchParams.has('q');
      const hasType = url.searchParams.has('type');

      if (hasQuery && !hasType) {
        url.searchParams.set('type', 'product');
        window.history.replaceState({}, '', url.toString());

        const sectionId = this.getAttribute('section-id');
        if (sectionId) {
          sectionRenderer.renderSection(sectionId, { cache: false, url });
        }
      }
    }
  }

  async #ensurePageIsFilled() {
    // Prevent concurrent calls and limit iterations
    if (this.#isEnsuringPageFilled) return;
    if (this.#currentIteration >= this.#maxIterations) {
      this.#currentIteration = 0;
      return;
    }

    const { cards, grid } = this.refs;

    if (!cards || !Array.isArray(cards) || cards.length === 0 || !grid) {
      this.#isEnsuringPageFilled = false;
      this.#currentIteration = 0;
      return;
    }

    this.#isEnsuringPageFilled = true;
    this.#currentIteration++;

    // Get current page from URL instead of cards
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = Number(urlParams.get('page')) || 1;
    const lastPage = Number(grid.dataset.lastPage || 1);

    // If we're on the last page, no need to check
    if (currentPage >= lastPage) {
      this.#isEnsuringPageFilled = false;
      this.#currentIteration = 0;
      return;
    }

    // Count products on current page only
    const currentPageProducts = cards.filter((card) => Number(card.dataset.page) === currentPage);

    if (currentPageProducts.length === 0) {
      this.#isEnsuringPageFilled = false;
      this.#currentIteration = 0;
      return;
    }

    // Get expected columns per row from CSS or data attribute
    let columnsPerRow = Number(grid.dataset.columnsPerRow);
    if (!columnsPerRow) {
      // Fallback: calculate from grid style or assume standard grid
      const gridStyle = window.getComputedStyle(grid);
      const gridCols = gridStyle.getPropertyValue('grid-template-columns');
      if (gridCols && gridCols !== 'none') {
        columnsPerRow = gridCols.split(' ').length;
      } else {
        // Ultimate fallback: use viewport-based assumption
        columnsPerRow = mediaQueryLarge.matches ? 4 : 2;
      }
    }

    // Calculate how many products we need to complete the grid
    const remainder = currentPageProducts.length % columnsPerRow;
    const neededProducts = remainder === 0 ? 0 : columnsPerRow - remainder;

    if (neededProducts === 0) {
      this.#isEnsuringPageFilled = false;
      this.#currentIteration = 0;
      return;
    }

    const nextPage = currentPage + 1;

    // Get next page products from cache or fetch them
    let nextPageProducts = this.#getGridForPageFromCache(nextPage);

    if (!nextPageProducts || nextPageProducts.length === 0) {
      // Fetch the next page if not cached
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage.toString());
      url.hash = '';

      try {
        const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
        if (pageContent) {
          this.pages.set(nextPage, pageContent);
          nextPageProducts = this.#getGridForPageFromCache(nextPage);
        }
      } catch (error) {
        console.error('Error fetching next page:', error);
        this.#isEnsuringPageFilled = false;
        this.#currentIteration = 0;
        return;
      }
    }

    if (nextPageProducts && nextPageProducts.length > 0) {
      // Get only the exact number of products we need
      const productsToAdd = Array.from(nextPageProducts).slice(0, neededProducts);

      if (productsToAdd.length > 0) {
        const currentProductIds = Array.from(cards).map(card => card.dataset.productId);
        const uniqueProductsToAdd = productsToAdd.filter(product => 
          !currentProductIds.includes(product.dataset.productId)
        );

        if (uniqueProductsToAdd.length > 0) {
          const lastCurrentPageCard = currentPageProducts[currentPageProducts.length - 1];
          if (lastCurrentPageCard) {
            const clonedProducts = uniqueProductsToAdd.map((el) => {
              const cloned = el.cloneNode(true);
              if (cloned instanceof Element) {
                cloned.setAttribute('data-page', currentPage.toString());
              }
              return cloned;
            });
            lastCurrentPageCard.after(...clonedProducts);

            this.#isEnsuringPageFilled = false;
            // Reset counter on successful addition
            this.#currentIteration = 0;
            return;
          }
        }
      }
    }

    this.#isEnsuringPageFilled = false;
    this.#currentIteration = 0;
  }

  /**
   * Gets grid items for a specific page from cache
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPageFromCache(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
